import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Medication, Calculate, Add } from '@mui/icons-material';
import {
  Treatment,
  getTreatmentDisplayName,
} from '../../types/treatment.types';

interface DrugTreatmentConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  treatment: Treatment | null;
  /** Called when user wants to add without calculation */
  onAddWithoutCalculation: () => void;
  /** Called when user wants to calculate dosage first */
  onCalculateDosage: () => void;
}

/**
 * Dialog shown when adding a drug treatment to ask whether to calculate dosage
 * or just add the treatment without calculations.
 */
const DrugTreatmentConfirmDialog: React.FC<DrugTreatmentConfirmDialogProps> = ({
  open,
  onClose,
  treatment,
  onAddWithoutCalculation,
  onCalculateDosage,
}) => {
  if (!treatment) {
    return null;
  }

  const treatmentName = getTreatmentDisplayName(treatment);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="drug-treatment-confirm-dialog-title"
    >
      <DialogTitle id="drug-treatment-confirm-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Medication sx={{ color: 'info.main' }} />
          <Typography variant="h6" component="div">
            Add Drug Treatment
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are adding <strong>{treatmentName}</strong> to the case.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Would you like to calculate the dosage for this drug, or add it
          without dosage calculations?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="text" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onAddWithoutCalculation}
          variant="outlined"
          startIcon={<Add />}
        >
          Add Without Calculation
        </Button>
        <Button
          onClick={onCalculateDosage}
          variant="contained"
          color="primary"
          startIcon={<Calculate />}
        >
          Calculate Dosage
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DrugTreatmentConfirmDialog;
